import { shallow, useShallow } from 'zustand/shallow'
import { createWithEqualityFn } from 'zustand/traditional'

import type { ReactNode } from 'react'

type SheetPanel = {
	title: string
	description?: string
	render: () => ReactNode | null
	className?: string
	side?: 'top' | 'bottom' | 'left' | 'right'
	onClose?: () => void
	onOpen?: () => void
	/** Return `false` to prevent the panel from closing (e.g. unsaved changes guard). */
	beforeClose?: () => boolean
}

type PanelState = {
	isOpen: boolean
	panel: SheetPanel
}

type PanelActions = {
	createPanel: (panel: Omit<SheetPanel, 'isOpen'>) => void
	/** Patch the current panel config without replacing it. */
	updatePanel: (patch: Partial<SheetPanel>) => void
	close: () => void
	reset: () => void
}

type PanelStore = PanelState & {
	actions: PanelActions
}

const initialState: PanelState = {
	isOpen: false,
	panel: {
		title: '',
		description: '',
		className: '',
		side: 'right',
		render: () => null,
	},
}

/**
 * The `use` prefix on this binding is LOAD-BEARING, not decoration. The React Compiler
 * classifies a callee as a hook BY NAME. Bound to a lowercase name, the selector call in
 * `usePanel` below compiled to a memo-cached branch — verified by running
 * `babel-plugin-react-compiler` over this module, the same plugin `vite.config.ts` loads
 * via `reactCompilerPreset()`. A cache hit skips zustand's `useSyncExternalStore`, which
 * is a conditional hook: the render calls fewer hooks than the last one and React throws
 * *Rendered fewer hooks than expected* or *Should have a queue*, in whichever component
 * mounted the hook and nowhere near this file.
 *
 * `usePanel` never actually fired that, but only by luck: `useShallow` returns a NEW
 * closure on every render, so the compiler's cache key never matched and the call always
 * re-ran. Hoisting that selector to a module constant would have armed it. The name is
 * the guard — same note at `features/custom-dashboards/stores/active-anchor-store.ts`.
 */
const usePanelStore = createWithEqualityFn<PanelStore>()(
	(set, get) => ({
		// Set initial state
		...initialState,

		actions: {
			createPanel: (panel: SheetPanel) => {
				const onOpen = get().panel.onOpen

				if (typeof onOpen === 'function') {
					onOpen()
				}

				return set((state) => ({
					...state,
					isOpen: true,
					panel: { ...state.panel, ...panel },
				}))
			},
			updatePanel: (patch: Partial<SheetPanel>) =>
				set((state) => ({
					...state,
					panel: { ...state.panel, ...patch },
				})),
			close: () => {
				const { onClose, beforeClose } = get().panel

				if (typeof beforeClose === 'function' && !beforeClose()) {
					return
				}

				if (typeof onClose === 'function') {
					onClose()
				}

				return set(initialState)
			},
			reset: () => set(initialState),
		},
	}),
	shallow,
)

/**
 * Exposes the current panel config (title, render, side, lifecycle callbacks).
 * `useShallow`-stabilized, so it only re-renders when a config field changes,
 * not on every store update.
 */
export const usePanel = () =>
	usePanelStore(
		useShallow((state) => {
			return {
				title: state.panel.title,
				description: state.panel.description,
				render: state.panel.render,
				className: state.panel.className,
				side: state.panel.side,
				onClose: state.panel.onClose,
				onOpen: state.panel.onOpen,
				beforeClose: state.panel.beforeClose,
			}
		}),
	)
/** Exposes only the `isOpen` flag, so it re-renders solely on open/close. */
export const useIsPanelOpen = () => usePanelStore((state) => state.isOpen)
/** Exposes the stable `actions` object only; never re-renders on state changes. */
export const usePanelActions = () => usePanelStore((state) => state.actions)
